"use client";

import { useDiscuss } from "@/context/DiscussConexts";
import { Routes } from "@/lib/routes";
import {
  type ContactFormData,
  ContactFormSchema,
} from "@/validation/contactFormSchema";
import {
  Select,
  SelectItem,
  SelectLabel,
  SelectPopover,
  SelectProvider,
} from "@ariakit/react";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import Link from "next/link";
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { Button } from "../Button";
import { Overlay } from "../Overlay";
import styles from "./ContactForm.module.css";

const subjects = [
  { value: "Обсудить проект" },
  { value: "Стать партнером" },
  { value: "Хочу в команду" },
];

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(
    null
  );
  const { isFormOpen, closeForm } = useDiscuss();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<ContactFormData>({ resolver: zodResolver(ContactFormSchema) });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...data, phone: data.phone || "" }),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitStatus("success");
        reset();
      } else {
        setIsSubmitting(false);
        setSubmitStatus("error");
      }
    } catch (_error) {
      setIsSubmitting(false);
      setSubmitStatus("error");
    }
  };

  const handleClose = () => {
    closeForm();
    const timeout = setTimeout(() => {
      setSubmitStatus(null);
    }, 500);
    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    setValue("subject", subjects[0].value);
  }, [setValue]);

  const errBool = {
    name: Boolean(errors.name),
    email: Boolean(errors.email),
    phone: Boolean(errors.phone),
    message: Boolean(errors.message),
  };

  const CloseButton = () => {
    return (
      <button
        onClick={handleClose}
        type="button"
        className={styles["close-button"]}
      >
        <svg
          role="img"
          aria-label="Закрыть форму"
          xmlns="http://www.w3.org/2000/svg"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <rect
            x="31.6655"
            y="20.2402"
            width="16.1538"
            height="2.69231"
            transform="rotate(135 31.6655 20.2402)"
            fill="#769FCD"
          />
          <rect
            x="29.7603"
            y="31.665"
            width="16.1538"
            height="2.69231"
            transform="rotate(-135 29.7603 31.665)"
            fill="#769FCD"
          />
        </svg>
      </button>
    );
  };

  if (!isFormOpen) {
    return null;
  }

  if (submitStatus === "success") {
    return (
      <>
        <PopoverContainer className={styles["custom-container"]}>
          <div className={styles["success-submit"]}>
            <CloseButton />
            <p>
              Спасибо, мы скоро <br />с вами свяжемся!
            </p>
            <Button
              onClick={handleClose}
              className={styles["submit-button"]}
              variant={"primary"}
            >
              Хорошо
            </Button>
          </div>
        </PopoverContainer>
        <Overlay onClick={handleClose} active={true} />
      </>
    );
  }

  if (submitStatus === "error") {
    return (
      <>
        <PopoverContainer className={styles["custom-container"]}>
          <div className={styles["error-submit"]}>
            <CloseButton />
            <p>Что-то пошло не так, пожалуйста, попробуйте снова</p>
            <Button
              onClick={() => setSubmitStatus(null)}
              className={styles["submit-button"]}
              variant={"error"}
            >
              Еще раз
            </Button>
          </div>
        </PopoverContainer>
        <Overlay onClick={handleClose} active={true} />
      </>
    );
  }

  return (
    <>
      <PopoverContainer>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={styles["contact-form"]}
        >
          <CloseButton />
          <h1>Заполните форму ниже</h1>
          <FieldWrapper>
            <Label htmlFor="name" error={errBool.name}>
              Ваше имя
            </Label>
            <Input {...register("name")} error={errBool.name} />
            {errors.name && (
              <p className={styles["error-message"]}>{errors.name.message}</p>
            )}
          </FieldWrapper>
          <FieldWrapper>
            <Label htmlFor="email" error={errBool.email}>
              Почта
            </Label>
            <Input {...register("email")} error={errBool.email} />
            {errors.email && (
              <p className={styles["error-message"]}>{errors.email.message}</p>
            )}
          </FieldWrapper>
          <FieldWrapper>
            <Label htmlFor="phone" error={errBool.phone}>
              Телефон
            </Label>
            <Input {...register("phone")} error={errBool.phone} />
            {errors.phone && (
              <p className={styles["error-message"]}>{errors.phone.message}</p>
            )}
          </FieldWrapper>
          <FieldWrapper>
            <SelectProvider defaultValue={subjects[0].value}>
              <SelectLabel className={styles.label}>Тема</SelectLabel>
              <Select className={styles.select} />
              <SelectPopover
                className={styles.popover}
                gutter={0}
                sameWidth={true}
                unmountOnHide={true}
              >
                {subjects.map((s) => (
                  <SelectItem
                    className={styles["select-item"]}
                    key={s.value}
                    value={s.value}
                    onClick={() => setValue("subject", s.value)}
                  >
                    {s.value}
                  </SelectItem>
                ))}
              </SelectPopover>
            </SelectProvider>
          </FieldWrapper>
          <FieldWrapper>
            <Label htmlFor="message" error={errBool.message}>
              Сообщение
            </Label>
            <Input {...register("message")} error={errBool.message} />
            {errors.message && (
              <p className={styles["error-message"]}>
                {errors.message.message}
              </p>
            )}
          </FieldWrapper>
          <FieldWrapper className={styles["form-footer"]}>
            <p className={styles.privacy}>
              Нажимая «Отправить» вы даете согласие на{" "}
              <Link href={Routes.PRIVACY} target="_blank">
                обработку персональных данных
              </Link>
            </p>
            <Button
              className={styles["submit-button"]}
              type="submit"
              variant={"primary"}
              disabled={isSubmitting}
            >
              Отправить
            </Button>
          </FieldWrapper>
        </form>
      </PopoverContainer>
      <Overlay onClick={handleClose} active={true} />
    </>
  );
};

const PopoverContainer = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <section className={classNames(styles["popover-container"], className)}>
      {children}
    </section>
  );
};

const FieldWrapper = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={classNames(styles["field-wrapper"], className)}>
      {children}
    </div>
  );
};

type LabelProps = {
  children: ReactNode;
  htmlFor: string;
  error: boolean;
} & ComponentPropsWithoutRef<"label">;

const Label = ({ children, htmlFor, error, ...props }: LabelProps) => {
  return (
    <label
      className={classNames(styles.label, {
        [styles["error-label"]]: error,
      })}
      htmlFor={htmlFor}
      {...props}
    >
      {children}
    </label>
  );
};

type InputProps = {
  error: boolean;
} & ComponentPropsWithoutRef<"input">;

const Input = ({ error, ...props }: InputProps) => {
  return (
    <input
      className={classNames(styles.input, {
        [styles["error-input"]]: error,
      })}
      {...props}
    />
  );
};
